/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import {
  AEMReactCompMap,
  VariableColumnContainer as VariableColumnContainerMolecule,
  updateAEMCQKeys,
  useEditorMode,
} from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { FC, lazy, Suspense } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { VariableColumnProps, VariableColumnContainerProps } from './VariableColumnContainer.types';
import { JSX } from 'react/jsx-runtime';
import React from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { getOffersComponent, buildItemProps } from '../../../../mi-offers-components/src/lib/helper';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getLeisureComponent } from '../../../../mi-leisure-components/src/utils/DestinationUtils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getCategoryComponent } from 'libs/mi-category-components/src/lib/helper';
import { getWCMModeFlagValue } from '@marriott/shared/mi-helper-utils';

export const VariableColumnContainerConfig = {
  emptyLabel: 'VariableColumnContainer',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/variablecolumncontainer`,
};

export const VariableColumnContainerComp: FC<VariableColumnProps> = (props: VariableColumnProps) => {
  const {
    header,
    headerTag,
    subHeader,
    ctaLabel,
    ctaLink,
    eyebrow,
    ctaType,
    openInNewTab,
    trackingProperties,
    styleclass,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    desktopColumns,
    tabletColumns,
    mobileColumns,
    reverseStacking,
    contentAlignment,
    headerFontSize,
    subFontSize,
    subHeaderTag,
    descriptionText,
    descFontSize,
  } = props;

  const { isInEditor } = useEditorMode();
  const updatedProps = updateAEMCQKeys(props);
  const cqItems = updatedProps['cqItems'];
  const wcmModeDisabled = getWCMModeFlagValue();

  const col = getColumnsObj(desktopColumns, tabletColumns, mobileColumns);

  const formVariableColumnArray = () => {
    const columnsArray = [];
    for (let i = 0; i < col.length; i++) {
      columnsArray.push([
        `variable-column-${i}`,
        `col-xl-${col?.[i].deskVal} col-md-${col?.[i].tabVal} col-${col?.[i].mobVal} column-containers`,
      ]);
    }
    return columnsArray;
  };

  const variableColumnArray = formVariableColumnArray();

  const mapper = AEMReactCompMap(props?.allowedComponents);

  // This function is used to render the component in authoring mode authorComponentWrapper
  const authorComponentWrapper = (index: number) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${variableColumnArray[index][0]}`}
        columnCount="12"
        gridClassNames={''}
        customClassName={''}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
        isInEditor={isInEditor}
      />
    );
  };

  // This function is used to render the component in end-user mode
  const publishComponentWrapper = (columnName: string, jsonData: any, columnClass: string) => {
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(columnName)) {
      const column = jsonData[columnName];
      const columnItems = column[':items'];
      const columnOrder: string[] = column[':itemsOrder'];
      const componentsArray: JSX.Element[] = [];

      columnOrder.forEach(itemKey => {
        if (Object.prototype.hasOwnProperty.call(columnItems, itemKey)) {
          const item = columnItems[itemKey];
          const itemType = item[':type']?.split('/').pop();
          if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
            const innerComp = mapper[itemType];
            const Component = lazy(() =>
              props?.componentPath === 'mi-offers-components'
                ? getOffersComponent(innerComp, itemType, item[':type'])
                : props?.componentPath === 'mi-leisure-components' && innerComp === 'DestinationsAccordion'
                ? getLeisureComponent(innerComp)
                : props?.componentPath === 'mi-category-components'
                ? getCategoryComponent(innerComp, item[':type'])
                : import(`../${innerComp}/index`).then(module => ({
                    default: module[`${innerComp}`],
                  }))
            );
            const itemProps =
              props?.componentPath === 'mi-offers-components'
                ? buildItemProps(item, itemType, innerComp, props)
                : { model: { ...item }, ...item };

            componentsArray.push(<Component {...itemProps} />);
          }
        }
      });

      if (componentsArray.length) {
        return (
          <li className={columnClass}>
            {componentsArray.map((child, index) => (
              <Suspense fallback={<></>} key={`${uuidV4()}`}>
                {React.isValidElement(child) ? React.cloneElement(child, { key: index + `-${uuidV4()}` }) : child}
              </Suspense>
            ))}
          </li>
        );
      } else {
        return null;
      }
    }
    return null;
  };

  const desktopColVals = desktopColumns ? desktopColumns.split(',') : [];

  return (
    <VariableColumnContainerMolecule
      componentId={componentId}
      subHeader={subHeader}
      styleclass={styleclass}
      eyebrow={eyebrow}
      trackingProperties={trackingProperties}
      ctaLabel={ctaLabel}
      header={header}
      headerTag={headerTag}
      ctaLink={ctaLink}
      openInNewTab={openInNewTab}
      ctaType={ctaType}
      isAuthor={isAuthorMode}
      desktopColumns={desktopColumns}
      tabletColumns={tabletColumns}
      mobileColumns={mobileColumns}
      reverseStacking={reverseStacking}
      contentAlignment={contentAlignment}
      headerFontSize={headerFontSize}
      subFontSize={subFontSize}
      subHeaderTag={subHeaderTag}
      descriptionText={descriptionText}
      descFontSize={descFontSize}
      isTenColumnView={isTenColumnView(desktopColVals)}
    >
      {isAuthorMode && !(wcmModeDisabled && props?.componentPath === 'mi-luxury-components')
        ? Array.from({ length: variableColumnArray.length }, (_, i) => (
            <div className={variableColumnArray[i][1]}>{authorComponentWrapper(i)}</div>
          ))
        : variableColumnArray.map((columnName: string[], i) => {
            return publishComponentWrapper(columnName[0], cqItems, variableColumnArray[i][1]);
          })}
    </VariableColumnContainerMolecule>
  );
};

export const VariableColumnEditableComponent = (props: any) => {
  const { model } = props;
  const cqType = model?.cqType || model?.[':type'];
  return (
    <EditableComponent config={VariableColumnContainerConfig} {...props}>
      <VariableColumnContainerComp {...props} componentName={cqType?.split('/')?.pop()} />
    </EditableComponent>
  );
};

export const VariableColumnContainer: FC<VariableColumnContainerProps> = (props: VariableColumnContainerProps) => {
  const { model } = props;
  return (
    <div
      className={clsx(
        [model?.styleclass?.includes('inverse') ? 'inverse' : ''],
        [model?.styleclass?.includes('fullbleed') ? 'm-container-fullbleed' : '']
      )}
      data-testid="variable-column-container"
      data-component-name="o-common-static-variablecolumncontainer"
    >
      <VariableColumnEditableComponent {...props} />
    </div>
  );
};

export const createColumn = (deskVal: number, tabVal: number, mobVal: number = 12) => {
  const item = {
    deskVal,
    tabVal,
    mobVal,
  };
  return item;
};

function isTenColumnView(desktopColVals: string[]) {
  if (desktopColVals.length !== 2) {
    return false;
  }

  const [totalCols, colValue] = desktopColVals;
  return totalCols === '1' && colValue === '10';
}

export const getColumnsObj = (desktopColumns = '', tabletColumns = '', mobileColumns = '') => {
  const col = [];
  if (desktopColumns !== '' || tabletColumns !== '' || mobileColumns !== '') {
    const [desktopColVals, tabletColVals] = [desktopColumns.split(','), tabletColumns.split(',')];

    switch (true) {
      case isTenColumnView(desktopColVals):
        {
          const xlVal = 10;
          const mdVal = 12;
          const smVal = 12;
          const item = createColumn(xlVal, mdVal, smVal);
          col.push(item);
        }
        break;
      case desktopColVals.length === 1 && tabletColVals.length === 1:
        {
          const deskTopCols = parseInt(desktopColumns);
          const tabCols = parseInt(tabletColumns);
          const mobCols = parseInt(mobileColumns);

          const xlVal = 12 / deskTopCols;
          const mdVal = 12 / tabCols;
          const smVal = 12 / mobCols;

          for (let i = 0; i < deskTopCols; i++) {
            const item = createColumn(xlVal, mdVal, smVal);
            col.push(item);
          }
        }
        break;
      case desktopColVals.length > 1 && tabletColVals.length > 1:
        {
          const mobCols = parseInt(mobileColumns);
          const smVal = 12 / mobCols;
          for (let i = 0; i < parseInt(desktopColVals[0]); i++) {
            const item = createColumn(parseInt(desktopColVals[i + 1]), parseInt(tabletColVals[i]), smVal);
            col.push(item);
          }
        }
        break;
      case desktopColVals.length > 1 && tabletColVals.length === 1:
        {
          const tabCols = parseInt(tabletColumns);
          const mdVal = 12 / tabCols + 1;
          const mobCols = parseInt(mobileColumns);
          const smVal = 12 / mobCols;
          for (let i = 0; i < parseInt(desktopColVals[0]); i++) {
            const item = createColumn(parseInt(desktopColVals[i + 1]), mdVal, smVal);
            col.push(item);
          }
        }
        break;
      case desktopColVals.length === 1 && tabletColVals.length > 1:
        {
          const deskTopCols = parseInt(desktopColumns);
          const xlVal = 12 / deskTopCols;
          const mobCols = parseInt(mobileColumns);
          const smVal = 12 / mobCols;
          for (let i = 0, j = 0; i < deskTopCols; i++, j++) {
            if (j === tabletColVals.length) {
              j = 0;
            }
            const item = createColumn(xlVal, parseInt(tabletColVals[j]), smVal);
            col.push(item);
          }
        }
        break;
    }
  }
  return col;
};
